import { cn } from "@/lib/utils";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";
import { Eye, EyeOff, RotateCcw } from "lucide-react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  HTMLInputTypeAttribute,
  ReactNode,
  useCallback,
  useState,
} from "react";
import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  RegisterOptions,
} from "react-hook-form";
import { ErrorMessage } from "./ErrorMessage";
import ResizeTextarea from "react-textarea-autosize";
import { Textarea } from "../shadcn/textarea";
import { UtcScheduleTimeHint } from "./UtcScheduleTimeHint";

// New Input
interface INoxusInput {
  /** Optional DOM id forwarded to the underlying input/textarea so an
   *  associated <label htmlFor=...> picks it up for a11y / testing tools. */
  id?: string;
  value?: string;
  label?: string;
  helperText?: ReactNode;
  tooltip?: string;
  placeholder?: string;
  disable?: boolean;
  hasError?: boolean;
  forcedName?: string;
  onChange: ChangeEventHandler<any>;
  onPaste?: ClipboardEventHandler<any>;
  onBlur?: FocusEventHandler<any>;
  onFocus?: FocusEventHandler<any>;
  onKeyDown?: (e: React.KeyboardEvent<any>) => any;
  isTextarea?: boolean;
  /** Visible rows for the textarea variant; forwarded to the native element. */
  rows?: number;
  /** Keep the textarea at `rows` instead of growing with what is typed. The
   *  shared Textarea sets `field-sizing-content`, so growing is the default. */
  isFixedHeight?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  customStyle?: any;
  fetchingDisplayOptions?: {
    onClick: () => any;
    isLoading: boolean;
  };
  isAutoResize?: boolean;
  type?: HTMLInputTypeAttribute;
  showTimezone?: boolean;
  showUtcTimeHint?: boolean;
  isResizable?: boolean;
  maxH?: string;
  height?: string;
  autoFocus?: boolean;
  name?: string;
  isOptional?: boolean;
  maxLength?: number;
  /** id of a <datalist> whose options the input suggests. */
  list?: string;
}

interface INoxusInputFieldProps {
  name: string;
  /** DOM id for the input. Defaults to `name`, which is what NoxusFormLabel
   *  already points its `htmlFor` at — without it the label referenced an id
   *  that did not exist, so clicking it focused nothing. */
  id?: string;
  tooltip?: string;
  helperText?: ReactNode;
  label?: string;
  placeholder?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  errors?: FieldErrors<any>;
  disable?: boolean;
  isTextarea?: boolean;
  isReadOnly?: boolean;
  showOptionalLabel?: boolean;
  forcedName?: string;
  defaultValue?: string;
  onFocus?: FocusEventHandler<any>;
  onChange?: ChangeEventHandler<any>;
  customStyle?: any;
  fetchingDisplayOptions?: {
    onClick: () => any;
    isLoading: boolean;
  };
  isAutoResize?: boolean;
  isResizable?: boolean;
  type?: HTMLInputTypeAttribute;
  showTimezone?: boolean;
  showUtcTimeHint?: boolean;
  autoFocus?: boolean;
  isOptional?: boolean;
  maxLength?: number;
  /** id of a <datalist> whose options the input suggests. */
  list?: string;
}

export const NoxusInput = ({
  id,
  value,
  label,
  helperText,
  tooltip,
  placeholder,
  forcedName,
  isTextarea,
  rows,
  isFixedHeight = false,
  isReadOnly = false,
  isRequired = false,
  customStyle,
  disable = false,
  hasError = false,
  onPaste,
  onChange,
  onFocus,
  onKeyDown,
  onBlur,
  fetchingDisplayOptions,
  isAutoResize = false,
  type = "text",
  isResizable = true,
  showOptionalLabel,
  autoFocus,
  showTimezone = false,
  showUtcTimeHint = false,
  name,
  isOptional,
  maxLength,
  list,
}: INoxusInput) => {
  const [show, setShow] = useState(false);

  const charCount = value?.length ?? 0;
  const atLimit = maxLength != null && charCount >= maxLength;

  const handleOnBlur = useCallback(
    (e: any) => {
      if (
        forcedName &&
        (e.target.value == undefined ||
          e.target.value.replace(/ /g, "").length === 0)
      ) {
        const eventCopy = { ...e, target: { ...e.target, value: forcedName } };
        onChange(eventCopy);
        if (onBlur) onBlur(eventCopy);
      } else {
        /* Trigger parent on blur */
        if (onBlur) onBlur(e);
      }
    },
    [forcedName, onBlur, onChange]
  );

  return (
    <div className="w-full">
      {label != undefined && (
        <div className="flex justify-between items-center">
          <NoxusFormLabel
            label={label}
            tooltip={tooltip}
            isRequired={isRequired}
            showOptionalLabel={showOptionalLabel}
            name={name}
          />
          {maxLength != null && (
            <span
              className={cn(
                "tabular-nums transition-colors text-xs",
                atLimit && "text-destructive font-medium"
              )}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}

      {isTextarea ? (
        <div className="relative">
          <Textarea
            id={id}
            rows={rows}
            aria-invalid={hasError}
            className={cn(
              "resize-none",
              !isAutoResize && isResizable && "resize-y",
              isFixedHeight && "field-sizing-fixed",
              customStyle
            )}
            autoFocus={autoFocus}
            value={value}
            placeholder={placeholder || ""}
            disabled={disable || isReadOnly}
            readOnly={isReadOnly}
            onChange={onChange}
            onBlur={handleOnBlur}
            onPaste={onPaste}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            maxLength={maxLength}
            {...(!isAutoResize && isResizable ? { as: ResizeTextarea } : {})}
          />
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="relative w-full">
            <div className="relative">
              <Input
                id={id}
                autoFocus={autoFocus}
                aria-invalid={hasError}
                className={cn(type === "password" && "pr-10", customStyle)}
                value={value}
                placeholder={placeholder || ""}
                disabled={disable || isReadOnly}
                readOnly={isReadOnly}
                onChange={onChange}
                onBlur={handleOnBlur}
                onPaste={onPaste}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                type={type === "password" ? (show ? "text" : "password") : type}
                maxLength={maxLength}
                list={list}
              />
              {type === "password" && (
                <Button
                  variant="ghost"
                  size="iconsm"
                  className="absolute top-1/2 -translate-y-1/2 right-1"
                  onClick={() => setShow(!show)}
                >
                  {show ? (
                    <EyeOff className="h-4 w-4" strokeWidth="1.33px" />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth="1.33px" />
                  )}
                </Button>
              )}
            </div>
            {type === "time" && showTimezone && (
              <span className="mt-1.5 text-xs font-regular">
                Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </span>
            )}
            {type === "time" && showUtcTimeHint && (
              <UtcScheduleTimeHint value={value} />
            )}
          </div>
          {fetchingDisplayOptions && (
            <Button
              variant="outline"
              size="icon"
              onClick={fetchingDisplayOptions.onClick}
              disabled={fetchingDisplayOptions.isLoading}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
      {helperText != null && !hasError && (
        <div className="mt-1.5 flex items-center justify-between text-xs font-regular text-muted-foreground">
          <span>{helperText ?? ""}</span>
        </div>
      )}
    </div>
  );
};

export const NoxusInputField = (props: INoxusInputFieldProps) => {
  const hasError = !!(
    props.errors &&
    props.errors[props.name] &&
    props.errors[props.name]?.message
  );

  return (
    <div className="flex w-full flex-col gap-1.5 items-start">
      <Controller
        control={props.control}
        name={props.name}
        defaultValue={props.defaultValue}
        rules={props.rules}
        render={({ field: { onChange, value, onBlur } }) => (
          <NoxusInput
            value={value || ""}
            helperText={hasError ? undefined : props.helperText}
            onBlur={onBlur}
            hasError={hasError}
            isRequired={Boolean(props.rules?.required)}
            {...props}
            id={props.id ?? props.name}
            onChange={(e) => {
              onChange(e);
              if (props.onChange) props.onChange(e);
            }}
          />
        )}
      />

      {hasError && (
        <ErrorMessage error={props.errors![props.name] as FieldError} />
      )}
    </div>
  );
};
